/** Evolve — a shape that mutates as you draw it, and evolves from stroke to stroke. */
import type { CreateModule, ModuleContext } from '../../core/module-api';
import { cfgNum } from '../../core/module-api';
import type { PathSegment, Point, Shape } from '../../core/scene';
import { defaultStyle } from '../../core/scene';
import { distance, resampleByArcLength } from '../../core/geometry';
import type { Rng } from '../../core/rng';

const ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 4c8 0 2 8 10 8"/><path d="M7 12c8 0 2 8 10 8"/><path d="M7 4v16"/><path d="M17 4v16"/></svg>';

function blob(n: number): Point[] {
  const out: Point[] = [];
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    out.push({ x: Math.cos(a) * 0.45, y: Math.sin(a) * 0.45 });
  }
  return out;
}

function smooth(p: Point[]): Point[] {
  const n = p.length;
  return p.map((c, i) => {
    const a = p[(i - 1 + n) % n];
    const b = p[(i + 1) % n];
    return { x: a.x * 0.25 + c.x * 0.5 + b.x * 0.25, y: a.y * 0.25 + c.y * 0.5 + b.y * 0.25 };
  });
}

/** Re-centre on the centroid and rescale to a fixed mean radius, so smooth()'s
 * contraction can't shrink the shape toward a dot over many strokes. */
function normalize(p: Point[]): Point[] {
  const n = p.length;
  let cx = 0;
  let cy = 0;
  for (const q of p) {
    cx += q.x;
    cy += q.y;
  }
  cx /= n;
  cy /= n;
  let r = 0;
  for (const q of p) r += Math.hypot(q.x - cx, q.y - cy);
  const s = 0.45 / (r / n || 1);
  return p.map((q) => ({ x: (q.x - cx) * s, y: (q.y - cy) * s }));
}

function mutate(p: Point[], rng: Rng, amt: number): Point[] {
  return normalize(smooth(p.map((q) => ({ x: q.x + (rng.next() * 2 - 1) * amt, y: q.y + (rng.next() * 2 - 1) * amt }))));
}

export function createEvolveShapes(): CreateModule {
  let pts: Point[] = [];
  let current: Point[] = blob(14);
  let drawing = false;

  return {
    manifest: {
      id: 'evolve',
      kind: 'create',
      name: 'Evolve',
      apiVersion: 1,
      description: 'A shape that mutates as you draw it, and evolves from one stroke to the next.',
      icon: ICON,
      capabilities: ['random'],
      params: [
        { kind: 'slider', key: 'mutation', label: 'Mutate', min: 0, max: 100, step: 1, default: 30 },
        { kind: 'slider', key: 'size', label: 'Size', min: 8, max: 140, step: 1, default: 46 },
        { kind: 'slider', key: 'spacing', label: 'Spacing', min: 10, max: 200, step: 1, default: 54 },
      ],
    },

    onPointerDown(s) {
      pts = [{ x: s.x, y: s.y }];
      drawing = true;
    },

    onPointerMove(s) {
      if (!drawing) return;
      const p = { x: s.x, y: s.y };
      const last = pts[pts.length - 1];
      if (!last || distance(last, p) >= 2) pts.push(p);
    },

    onPointerUp() {
      drawing = false;
    },

    getLiveShapes(ctx: ModuleContext): Shape[] {
      if (pts.length === 0) return [];
      const amt = (cfgNum(ctx.config, 'mutation', 30) / 100) * 0.22;
      const size = cfgNum(ctx.config, 'size', 46);
      const spacing = cfgNum(ctx.config, 'spacing', 54);
      const spots = resampleByArcLength(pts, spacing);
      const out: Shape[] = [];
      let shape = current.map((p) => ({ ...p }));
      spots.forEach((spot, i) => {
        if (i > 0 && amt > 0) shape = mutate(shape, ctx.rng, amt);
        const segs: PathSegment[] = [];
        shape.forEach((p, k) => {
          const x = spot.p.x + p.x * size;
          const y = spot.p.y + p.y * size;
          segs.push(k === 0 ? { t: 'M', p: { x, y } } : { t: 'L', p: { x, y } });
        });
        segs.push({ t: 'Z' });
        const style = defaultStyle();
        style.color = { ...ctx.ink };
        style.lineWidth = 2;
        out.push(ctx.newShape({ path: { segments: segs }, style }));
      });
      return out;
    },

    onCommit(ctx) {
      const amt = (cfgNum(ctx.config, 'mutation', 30) / 100) * 0.22;
      if (amt > 0) current = mutate(current, ctx.rng, amt);
      pts = [];
    },

    onClear() {
      pts = [];
    },

    onReselect() {
      current = blob(14);
    },
  };
}
